/**
 * Carries a selection dot from one radio to another as burning ash: the old dot
 * flares and breaks apart, a few ember-headed streams ride a shared gust and
 * leave smoke and falling ash behind them, and the new dot lands hot and cools
 * to its resting color. Purely decorative; the real selection state changes
 * immediately and stays in the DOM for keyboards and screen readers.
 *
 * Drawn on one transient canvas so trails, smoke and flakes can number in the
 * hundreds without touching layout.
 */

type Point = { x: number; y: number };
type Rgb = [number, number, number];

interface Sample { x: number; y: number; t: number }

interface Strand {
  from: Point;
  bend: Point;
  to: Point;
  start: number;
  duration: number;
  normal: Point;
  spread: number;
  wave: number;
  freq: number;
  phase: number;
  width: number;
  history: Sample[];
  head: Point;
  landed: boolean;
  smokeIn: number;
  ashIn: number;
  sparkIn: number;
  /** Set when a real element flies at the front, so no glowing dot leads the stream. */
  headless?: boolean;
}

interface Mote {
  kind: "smoke" | "ash" | "spark";
  x: number;
  y: number;
  vx: number;
  vy: number;
  born: number;
  life: number;
  size: number;
  spin: number;
  spinRate: number;
}

const STRANDS = 5;
const TRAIL_MS = 320;
const GRAVITY = 240;

// Design tokens from index.css / tailwind.config.js. The heads glow pale
// shimmer inside a devil-blue rim, cool through devil to muted smoke, and the
// ash settles to the dot's own fill (ocean-mystification).
const SHIMMER: Rgb = [216, 234, 247]; // --ocean-shimmer
const HOT: Rgb = [101, 151, 184]; // --ocean-devil-light
const EMBER: Rgb = [74, 124, 157]; // --ocean-devil
const SMOKE: Rgb = [90, 110, 122]; // --muted
const ASH_HOT: Rgb = EMBER;
const LAND_FLASH = "#6597b8";
const LAND_GLOW = "rgba(74, 124, 157, 0.32)";
const LAND_GLOW_CLEAR = "rgba(74, 124, 157, 0)";
const MAX_CANVAS_PIXELS = 6_000_000;

type Box = { left: number; top: number; right: number; bottom: number };

let canvas: HTMLCanvasElement | null = null;
let bounds: Box | null = null;
let ctx: CanvasRenderingContext2D | null = null;
let sprites: { glow: HTMLCanvasElement; smoke: HTMLCanvasElement; warmSmoke: HTMLCanvasElement } | null = null;
let strands: Strand[] = [];
let motes: Mote[] = [];
let ashColor: Rgb = [52, 66, 101];
let frame = 0;
let lastStep = 0;
let arriving: { animation: Animation; popsAt: number } | null = null;

const rand = (min: number, max: number) => min + Math.random() * (max - min);
const clamp01 = (n: number) => Math.min(1, Math.max(0, n));
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
const easeInOutSine = (t: number) => -(Math.cos(Math.PI * t) - 1) / 2;
// Leaves the dot with a shove, glides, then settles without overshoot.
const flightEase = (t: number) => easeOutCubic(t) * 0.55 + easeInOutSine(t) * 0.45;

const mix = (a: Rgb, b: Rgb, t: number): Rgb => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
  a[2] + (b[2] - a[2]) * t,
];
const rgba = (c: Rgb, a: number) => `rgba(${c[0] | 0}, ${c[1] | 0}, ${c[2] | 0}, ${a})`;

function parseRgb(color: string): Rgb | null {
  const m = color.match(/rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/);
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
}

function centerOf(el: Element): Point | null {
  const r = el.getBoundingClientRect();
  if (r.width === 0 || r.height === 0) return null;
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

function radialSprite(stops: [number, string][]) {
  const sprite = document.createElement("canvas");
  sprite.width = sprite.height = 64;
  const g = sprite.getContext("2d");
  if (!g) return sprite;
  const gradient = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  for (const [offset, color] of stops) gradient.addColorStop(offset, color);
  g.fillStyle = gradient;
  g.fillRect(0, 0, 64, 64);
  return sprite;
}

/**
 * Sizes the canvas to the area the effect can reach, in the same client
 * coordinates getBoundingClientRect reports for the dots. Measuring the placed
 * canvas, rather than trusting innerWidth or 100vw, keeps the drawing on the
 * dots when a phone is pinch-zoomed or showing the desktop layout.
 */
function fitCanvas(region: Box) {
  if (!canvas || !canvas.isConnected) {
    canvas = document.createElement("canvas");
    canvas.className = "dust-layer";
    canvas.setAttribute("aria-hidden", "true");
    ctx = canvas.getContext("2d");
    document.body.appendChild(canvas);
    bounds = null;
  }
  const box = bounds
    ? {
        left: Math.min(bounds.left, region.left),
        top: Math.min(bounds.top, region.top),
        right: Math.max(bounds.right, region.right),
        bottom: Math.max(bounds.bottom, region.bottom),
      }
    : region;
  bounds = box;
  const width = Math.max(1, Math.ceil(box.right - box.left));
  const height = Math.max(1, Math.ceil(box.bottom - box.top));
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  canvas.style.transform = "none";
  // A fixed element's origin is not always the dots' client origin (pinch
  // zoom, transformed ancestors), so place it, measure where it landed, and
  // shift it onto the box.
  const placed = canvas.getBoundingClientRect();
  const kx = placed.width / width || 1;
  const ky = placed.height / height || 1;
  canvas.style.width = `${width / kx}px`;
  canvas.style.height = `${height / ky}px`;
  canvas.style.transform = `translate(${(box.left - placed.left) / kx}px, ${(box.top - placed.top) / ky}px)`;
  const rect = canvas.getBoundingClientRect();

  const zoom = window.visualViewport?.scale ?? 1;
  const ratio = Math.min(
    (window.devicePixelRatio || 1) * Math.max(1, zoom),
    3,
    Math.sqrt(MAX_CANVAS_PIXELS / (width * height)),
  );
  canvas.width = Math.round(width * ratio);
  canvas.height = Math.round(height * ratio);
  const sx = canvas.width / (rect.width || width);
  const sy = canvas.height / (rect.height || height);
  ctx?.setTransform(sx, 0, 0, sy, -rect.left * sx, -rect.top * sy);

  sprites ??= {
    glow: radialSprite([
      [0, rgba(SHIMMER, 1)],
      [0.22, rgba(HOT, 0.85)],
      [0.55, rgba(EMBER, 0.24)],
      [1, rgba(EMBER, 0)],
    ]),
    smoke: radialSprite([
      [0, rgba(SMOKE, 1)],
      [0.45, rgba(SMOKE, 0.45)],
      [1, rgba(SMOKE, 0)],
    ]),
    warmSmoke: radialSprite([
      [0, rgba(HOT, 1)],
      [0.45, rgba(HOT, 0.4)],
      [1, rgba(HOT, 0)],
    ]),
  };
  return ctx;
}

function teardown() {
  cancelAnimationFrame(frame);
  frame = 0;
  lastStep = 0;
  canvas?.remove();
  canvas = null;
  ctx = null;
  bounds = null;
  strands = [];
  motes = [];
}

function pointOnPath(s: Strand, p: number): Point {
  const inv = 1 - p;
  // Streams fan out from the shared gust and pinch back together at both dots.
  const envelope = Math.sin(Math.PI * p);
  const lateral = (s.spread + s.wave * Math.sin(2 * Math.PI * s.freq * p + s.phase)) * envelope;
  return {
    x: inv * inv * s.from.x + 2 * inv * p * s.bend.x + p * p * s.to.x + s.normal.x * lateral,
    y: inv * inv * s.from.y + 2 * inv * p * s.bend.y + p * p * s.to.y + s.normal.y * lateral,
  };
}

function emit(kind: Mote["kind"], at: Point, vx: number, vy: number, now: number) {
  if (kind === "smoke") {
    motes.push({
      kind, x: at.x + rand(-2, 2), y: at.y + rand(-2, 2),
      vx: vx * 0.1 + rand(-14, 14), vy: vy * 0.1 + rand(-26, -8),
      born: now, life: rand(520, 780), size: rand(13, 19), spin: rand(0, Math.PI * 2), spinRate: rand(-3, 3),
    });
  } else if (kind === "ash") {
    motes.push({
      kind, x: at.x + rand(-3, 3), y: at.y + rand(-3, 3),
      vx: vx * rand(0.12, 0.3) + rand(-40, 40), vy: vy * rand(0.12, 0.3) + rand(-50, 10),
      born: now, life: rand(520, 900), size: rand(1.6, 3.4), spin: rand(0, Math.PI * 2), spinRate: rand(-14, 14),
    });
  } else {
    motes.push({
      kind, x: at.x, y: at.y,
      vx: vx * rand(0.45, 0.85) + rand(-60, 60), vy: vy * rand(0.45, 0.85) + rand(-60, 60),
      born: now, life: rand(160, 320), size: rand(5, 9), spin: 0, spinRate: 0,
    });
  }
}

function step(now: number) {
  const g = ctx;
  if (!g || !sprites || !canvas?.isConnected) return teardown();
  g.save();
  g.setTransform(1, 0, 0, 1, 0, 0);
  g.clearRect(0, 0, canvas.width, canvas.height);
  g.restore();

  for (const s of strands) {
    if (now < s.start || s.landed) continue;
    const t = clamp01((now - s.start) / s.duration);
    const head = pointOnPath(s, flightEase(t));
    const last = s.history[s.history.length - 1];
    const dt = last ? Math.max(1, now - last.t) / 1000 : 1 / 60;
    const vx = last ? (head.x - last.x) / dt : 0;
    const vy = last ? (head.y - last.y) / dt : 0;
    const travelled = last ? Math.hypot(head.x - last.x, head.y - last.y) : 0;

    // Shed smoke, ash and sparks by distance so slow and fast flights read alike.
    s.smokeIn -= travelled;
    s.ashIn -= travelled;
    s.sparkIn -= travelled;
    while (s.smokeIn <= 0) { emit("smoke", head, vx, vy, now); s.smokeIn += rand(8, 12); }
    while (s.ashIn <= 0) { emit("ash", head, vx, vy, now); s.ashIn += rand(16, 26); }
    while (s.sparkIn <= 0) { emit("spark", head, vx, vy, now); s.sparkIn += rand(26, 40); }

    s.history.push({ x: head.x, y: head.y, t: now });
    s.head = head;
    if (t >= 1) {
      s.landed = true;
      for (let i = 0; i < 3; i++) emit("spark", head, rand(-90, 90), rand(-110, 30), now);
      emit("smoke", head, 0, 0, now);
    }
  }
  for (const s of strands) while (s.history.length && now - s.history[0].t > TRAIL_MS) s.history.shift();

  motes = motes.filter((m) => now - m.born < m.life);
  const dt = lastStep ? Math.min(0.05, Math.max(0, now - lastStep) / 1000) : 1 / 60;
  lastStep = now;

  for (const m of motes) {
    if (m.kind !== "smoke") continue;
    const age = (now - m.born) / m.life;
    m.x += m.vx * dt + Math.sin(m.spin + age * 5) * 15 * dt;
    m.y += m.vy * dt;
    m.vx *= Math.pow(0.16, dt);
    m.vy *= Math.pow(0.16, dt);
    const radius = m.size * (0.25 + 0.75 * easeOutCubic(age));
    const alpha = 0.2 * Math.sin(Math.PI * Math.min(1, age * 1.15)) * (1 - age);
    const warmth = clamp01(1 - (now - m.born) / 200);
    g.globalAlpha = alpha * warmth;
    g.drawImage(sprites.warmSmoke, m.x - radius, m.y - radius, radius * 2, radius * 2);
    g.globalAlpha = alpha * (1 - warmth);
    g.drawImage(sprites.smoke, m.x - radius, m.y - radius, radius * 2, radius * 2);
  }
  g.globalAlpha = 1;

  // Tapered ribbons: hot at the head, cooling to smoke at the tail.
  g.lineCap = "round";
  for (const s of strands) {
    const h = s.history;
    for (let i = 1; i < h.length; i++) {
      const u = clamp01(1 - (now - h[i].t) / TRAIL_MS);
      if (u <= 0) continue;
      const color = u > 0.55 ? mix(EMBER, HOT, (u - 0.55) / 0.45) : mix(SMOKE, EMBER, u / 0.55);
      g.strokeStyle = rgba(color, 0.6 * Math.pow(u, 1.4));
      g.lineWidth = Math.max(0.3, s.width * Math.pow(u, 0.7));
      g.beginPath();
      g.moveTo(h[i - 1].x, h[i - 1].y);
      g.lineTo(h[i].x, h[i].y);
      g.stroke();
    }
  }

  for (const m of motes) {
    if (m.kind !== "ash") continue;
    const age = (now - m.born) / m.life;
    m.vx *= Math.pow(0.12, dt);
    m.vy = m.vy * Math.pow(0.12, dt) + GRAVITY * dt * 0.6;
    m.x += m.vx * dt + Math.sin(m.spin * 0.7) * 21 * dt;
    m.y += m.vy * dt;
    m.spin += m.spinRate * dt;
    const cooled = clamp01((now - m.born) / 180);
    g.fillStyle = rgba(mix(ASH_HOT, ashColor, cooled), 0.9 * (1 - Math.pow(age, 2)));
    g.save();
    g.translate(m.x, m.y);
    g.rotate(m.spin);
    // Flatten on one axis as it spins so each flake tumbles like a leaf.
    g.scale(Math.max(0.2, Math.abs(Math.cos(m.spin * 1.3))), 1);
    g.fillRect(-m.size / 2, -m.size * 0.35, m.size, m.size * 0.7);
    g.restore();
  }

  for (const m of motes) {
    if (m.kind !== "spark") continue;
    const age = (now - m.born) / m.life;
    m.vx *= Math.pow(0.0018, dt);
    m.vy = m.vy * Math.pow(0.0018, dt) + GRAVITY * dt * 0.3;
    m.x += m.vx * dt;
    m.y += m.vy * dt;
    const radius = m.size * (1 - age * 0.6);
    g.globalAlpha = 0.9 * (1 - age);
    g.drawImage(sprites.glow, m.x - radius, m.y - radius, radius * 2, radius * 2);
  }

  for (const s of strands) {
    if (now < s.start || s.landed || s.headless) continue;
    const flicker = 0.85 + Math.random() * 0.15;
    const radius = (s.width * 3.4 + 4) * flicker;
    g.globalAlpha = 1;
    g.drawImage(sprites.glow, s.head.x - radius, s.head.y - radius, radius * 2, radius * 2);
  }
  g.globalAlpha = 1;

  const busy = motes.length > 0 || strands.some((s) => !s.landed || s.history.length > 1);
  if (busy) frame = requestAnimationFrame(step);
  else teardown();
}

const CHIP_STRANDS = 4;
const CHIP_SAMPLES = 32;
const CHIP_LAND_MS = 460;

/**
 * Flies a real element from `from` to where it is laid out, on the same ash as
 * the dots. The element is the leading tip: it tilts into the gust and runs hot
 * with the streams pouring out behind it, then flares as it lands. Decorative,
 * and the element is already in its final place for anything but the eye.
 */
export function playChipFlight(from: { left: number; top: number; width: number; height: number }, chip: HTMLElement) {
  if (typeof chip.animate !== "function") return;
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
  const end = centerOf(chip);
  if (!end) return;

  const now = performance.now();
  const origin = { x: from.left + from.width / 2, y: from.top + from.height / 2 };
  const dist = Math.hypot(end.x - origin.x, end.y - origin.y);
  const duration = Math.min(820, 480 + dist * 0.34);
  const length = dist || 1;
  const normal = { x: (origin.y - end.y) / length, y: (end.x - origin.x) / length };
  const lift = Math.min(120, 34 + dist * 0.22);
  const gust = { x: (origin.x + end.x) / 2, y: (origin.y + end.y) / 2 - lift };

  const flight: Strand = {
    from: origin, bend: gust, to: end, start: now, duration, normal,
    spread: 0, wave: 0, freq: 1, phase: 0, width: 0, history: [], head: origin,
    landed: false, smokeIn: 0, ashIn: 0, sparkIn: 0, headless: true,
  };
  const lean = end.x >= origin.x ? 1 : -1;
  const frames: Keyframe[] = [];
  for (let i = 0; i <= CHIP_SAMPLES; i++) {
    const t = i / CHIP_SAMPLES;
    const at = pointOnPath(flight, flightEase(t));
    const arc = Math.sin(Math.PI * t);
    const heat = clamp01((1 - t) / 0.2);
    frames.push({
      offset: t,
      transform: `translate(${at.x - end.x}px, ${at.y - end.y}px) rotate(${lean * 7 * arc}deg) scale(${1 + 0.07 * arc})`,
      opacity: clamp01(t / 0.1),
      borderColor: rgba(mix(HOT, EMBER, 1 - heat), 0.4 + 0.6 * heat),
      boxShadow: `0 0 ${18 * heat}px ${2 * heat}px ${rgba(HOT, 0.6 * heat)}`,
    });
  }
  chip.animate(frames, { duration, easing: "linear" });
  // Lands hot: the corner flares and a ring cools outward.
  chip.animate(
    [
      { backgroundColor: rgba(SHIMMER, 1), boxShadow: `0 0 0 0 ${LAND_GLOW}` },
      { boxShadow: `0 0 0 10px ${LAND_GLOW_CLEAR}` },
    ],
    { duration: CHIP_LAND_MS, delay: duration, easing: "cubic-bezier(0.2, 0, 0, 1)" },
  );

  if (!document.createElement("canvas").getContext) return;
  const reach = {
    left: Math.min(from.left, end.x) - 140,
    top: Math.min(origin.y, end.y) - lift - 110,
    right: Math.max(from.left + from.width, end.x) + 140,
    bottom: Math.max(origin.y, end.y) + 220,
  };
  if (!fitCanvas(reach)) return;

  const fan = Math.min(from.height, chip.getBoundingClientRect().height);
  for (let i = 0; i < CHIP_STRANDS; i++) {
    strands.push({
      ...flight,
      // A pinch behind the chip, so the streams trail out of it rather than past it.
      bend: { x: gust.x + rand(-6, 6), y: gust.y + rand(-8, 8) },
      duration: duration * rand(1.02, 1.08),
      spread: (i / (CHIP_STRANDS - 1) - 0.5) * fan * rand(0.8, 1.2),
      wave: rand(1.5, 4),
      freq: rand(0.8, 1.6),
      phase: rand(0, Math.PI * 2),
      width: rand(2, 3.4),
      history: [],
      smokeIn: rand(0, 8),
      ashIn: rand(4, 18),
      sparkIn: rand(8, 26),
    });
  }

  // The row it left burns along its width instead of bursting from one point.
  for (let i = 0; i < 14; i++) {
    const at = { x: from.left + rand(0.05, 0.95) * from.width, y: from.top + rand(0.2, 0.8) * from.height };
    emit("ash", at, ((end.x - origin.x) / length) * 220 + rand(-120, 120), ((end.y - origin.y) / length) * 220 + rand(-160, 40), now);
  }
  for (let i = 0; i < 4; i++) emit("spark", origin, rand(-160, 160), rand(-200, 40), now);

  if (!frame) frame = requestAnimationFrame(step);
}

export function playDustTransfer(fromDot: Element, toDot: Element) {
  if (typeof document === "undefined") return;
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
  const toFill = toDot.querySelector<HTMLElement>("[data-dot-fill]");
  const fromFill = fromDot.querySelector<HTMLElement>("[data-dot-fill]");
  if (!toFill || typeof toFill.animate !== "function") return;
  if (!document.createElement("canvas").getContext) return;
  const end = centerOf(toDot);
  if (!end) return;

  const now = performance.now();
  const airborne = strands.filter((s) => !s.landed && now >= s.start);
  // If the previous dot was picked away from before its ash landed, it never
  // showed a fill, so there is nothing to burn: the streams just change course.
  const previousNeverFilled = arriving !== null && (arriving.animation.currentTime ?? 0) < arriving.popsAt;
  arriving?.animation.cancel();
  arriving = null;

  const fromCenter = centerOf(fromDot);
  const origin = previousNeverFilled && airborne.length ? airborne[0].head : fromCenter;
  if (!origin) return;

  const fillColor = getComputedStyle(toFill).backgroundColor || "rgb(52, 66, 101)";
  ashColor = parseRgb(fillColor) ?? ashColor;
  const dotRadius = toDot.getBoundingClientRect().width / 2;

  const dist = Math.hypot(end.x - origin.x, end.y - origin.y);
  const duration = Math.min(760, 440 + dist * 0.34);
  const chord = { x: end.x - origin.x, y: end.y - origin.y };
  const length = Math.hypot(chord.x, chord.y) || 1;
  const normal = { x: -chord.y / length, y: chord.x / length };
  // Every stream bends around the same gust so the cloud reads as one drift.
  const lift = Math.min(120, 34 + dist * 0.22);
  const gust = { x: (origin.x + end.x) / 2 + rand(-0.06, 0.06) * dist, y: (origin.y + end.y) / 2 - lift };
  const maxSpread = Math.min(26, 6 + dist * 0.06);
  // Room for the arc, the departure burst, and ash that falls after landing.
  const reach = {
    left: Math.min(origin.x, end.x) - 140,
    top: Math.min(origin.y, end.y) - lift - 110,
    right: Math.max(origin.x, end.x) + 140,
    bottom: Math.max(origin.y, end.y) + 220,
  };
  if (!fitCanvas(reach)) return;

  const next: Strand[] = [];
  for (let i = 0; i < STRANDS; i++) {
    const reused = airborne[i];
    const angle = rand(0, Math.PI * 2);
    const r = Math.sqrt(Math.random()) * dotRadius * 0.7;
    const from = reused?.head ?? { x: origin.x + Math.cos(angle) * r, y: origin.y + Math.sin(angle) * r };
    const lane = STRANDS > 1 ? i / (STRANDS - 1) - 0.5 : 0;
    next.push({
      from,
      bend: { x: gust.x + rand(-10, 10), y: gust.y + rand(-14, 14) },
      to: { x: end.x + rand(-1.5, 1.5), y: end.y + rand(-1.5, 1.5) },
      start: reused ? now : now + rand(0, 70),
      duration: duration * rand(0.86, 1.1),
      normal,
      spread: lane * 2 * maxSpread * rand(0.7, 1),
      wave: rand(2.5, 6),
      freq: rand(0.8, 1.6),
      phase: rand(0, Math.PI * 2),
      width: rand(2, 3.4),
      history: reused?.history ?? [],
      head: from,
      landed: false,
      smokeIn: rand(0, 8),
      ashIn: rand(4, 18),
      sparkIn: rand(8, 26),
    });
  }
  // Streams that already landed keep draining their trails; the rest are replaced.
  strands = strands.filter((s) => s.landed && s.history.length > 1).concat(next);

  if (fromCenter && !previousNeverFilled) {
    for (let i = 0; i < 10; i++) {
      const a = rand(0, Math.PI * 2);
      const r = Math.sqrt(Math.random()) * dotRadius;
      const at = { x: fromCenter.x + Math.cos(a) * r, y: fromCenter.y + Math.sin(a) * r };
      emit("ash", at, (chord.x / length) * 260 + Math.cos(a) * 160, (chord.y / length) * 260 + Math.sin(a) * 160 - 120, now);
    }
    for (let i = 0; i < 4; i++) emit("spark", fromCenter, rand(-160, 160), rand(-200, 40), now);
    fromFill?.animate?.(
      [
        { transform: "scale(1)", opacity: 1, backgroundColor: fillColor },
        { transform: "scale(1.22)", opacity: 0.85, backgroundColor: LAND_FLASH, offset: 0.35 },
        { transform: "scale(0.35)", opacity: 0, backgroundColor: LAND_FLASH },
      ],
      { duration: 240, easing: "cubic-bezier(0.2, 0, 0, 1)" },
    );
  }

  // The new dot catches as most of the streams arrive, flashes hot, and cools.
  const arrivals = next.map((s) => s.start - now + s.duration).sort((a, b) => a - b);
  const popsAt = arrivals[Math.floor(arrivals.length * 0.6)] - 40;
  const animation = toFill.animate(
    [
      { transform: "scale(0)", opacity: 0, backgroundColor: LAND_FLASH, boxShadow: `0 0 0 0 ${LAND_GLOW}` },
      { transform: "scale(1.16)", opacity: 1, backgroundColor: LAND_FLASH, boxShadow: `0 0 0 4px ${LAND_GLOW}`, offset: 0.3 },
      { transform: "scale(1)", opacity: 1, backgroundColor: fillColor, boxShadow: `0 0 0 9px ${LAND_GLOW_CLEAR}` },
    ],
    { duration: 520, delay: popsAt, easing: "cubic-bezier(0.2, 0, 0, 1)", fill: "backwards" },
  );
  arriving = { animation, popsAt };
  animation.onfinish = () => { if (arriving?.animation === animation) arriving = null; };

  if (!frame) frame = requestAnimationFrame(step);
}
